import type { DrawContext } from './draw';
import type { View } from './project';
import type { SkyField } from './field';
import type { EdgeFan } from './fan';
import type { LabelSpace } from './labels';
import { skyLOD } from './sky';
import type { SkyBands } from './sky';
import { drawSkyClumps, drawSkyDiscs, drawSkyDots, skyCounts } from './sky-draw';
import type { SkyCounts, SkyPaint } from './sky-draw';
import { drawSkyEdges } from './sky-edges';
import type { EdgeFocus, EdgePaint } from './sky-edges';

/*
 * WHICH SKY TO DRAW — the dispatcher over the four level-of-detail bands.
 *
 * Ported from `reference/universe-v8/engine.js` — `drawSky` (1005–1113). The three bands themselves
 * are `sky-draw.ts`; this is the piece that chooses between them, and it turned out never to have
 * been written: the ledger said `drawSky` was done because all four bands were, which is not the
 * same claim. Found while wiring the caller that runs the eleven layers of `drawStatic` in order —
 * which is exactly what that caller exists to catch.
 *
 * THE BANDS ARE NOT DEGRADED VERSIONS OF EACH OTHER. Each answers a different question, because at
 * each range the reader is asking a different one:
 *
 *   band 3   pulled right back — one soft blob per clump: WHERE IS ANYTHING
 *   band 2   a dot each, four fills for the whole sky: HOW MUCH IS THERE
 *   band 1   real discs in their scriptures' colours: WHAT KIND OF THING
 *   band 0   discs, corpus rings, and a few names: WHICH ONE IS THIS
 *
 * That is why band 2 is not simply band 1 with smaller circles. A dot has no colour worth reading
 * at that size, so the layer stops trying and batches by corpus instead — one fill per scripture
 * for ten thousand names.
 *
 * THE STRAND LAYER IS SWITCHED OFF BY BAND, NOT THINNED BY CROWD, and their own comment says so.
 * Beyond band 1 there is no web at all: a tie between two dots too small to tell apart is a line
 * between nothing and nothing.
 */

/** What the frame needs to know beyond the paint: what to skip, and how many ties are affordable. */
export interface SkyFrame {
  /** Whether names off screen are culled. Their `cullOffscreen` prop, on unless a caller says not. */
  cull: boolean;
  /** Whether the web is drawn at all. Their `showEdges` prop. */
  showEdges: boolean;
  /** The profiler's own switches, which run a frame with one layer removed to price it. */
  skipEdges?: boolean;
  skipNodes?: boolean;
  /** Whether to use the level-of-detail bands at all — off, everything draws at full detail. */
  useLOD?: boolean;
}

/** What one frame of sky cost and what it put on the screen. */
export interface SkyFrameResult extends SkyCounts {
  /** Strands drawn, or zero when the band has switched them off. */
  strands: number;
  /** Whether the strand layer ran at all — zero strands means two different things without it. */
  strandPath: boolean;
  /** How many names reached the screen, for the label budget to measure itself against. */
  visible: number;
}

/**
 * THE ZOOM BELOW WHICH THERE IS NO WEB, even inside the bands that have one.
 *
 * An argument, because a zoom is pixels per world unit — the nineteenth this port has lifted out.
 *
 * UNREACHABLE ON THEIR OWN BANDS, and kept anyway. The web is drawn in bands 0 and 1, which begin
 * at a zoom of 0.22, and the gate asks for more than 0.16 — so wherever it is consulted it is
 * already true. Found by lowering it and watching nothing change, the same way the stars' widest
 * dot and the nodes' need-floor turned out to be dead. It stays because a caller who switches the
 * bands off has no band to be stopped by, and then this is the only thing left.
 */
export interface SkyFrameZooms {
  strandsFrom: number;
  /** Where the four level-of-detail bands begin. Three absolute zooms, so three arguments. */
  bands: SkyBands;
}

/** The band at which the strand layer is switched off entirely, whatever the zoom. */
const STRANDS_BAND = 1;

/**
 * Draw the streamed field at whatever detail this range deserves.
 *
 * Returns the counts rather than writing them into a shared `perf` object, so the caller decides
 * what to do with them and two frames cannot race over the same numbers.
 */
export function drawSky(
  ctx: DrawContext,
  view: View,
  field: SkyField,
  paint: SkyPaint,
  frame: SkyFrame,
  zooms: SkyFrameZooms,
  edges: { paint: EdgePaint; fan: EdgeFan; stamp: number; focus?: EdgeFocus },
  labels: LabelSpace,
): SkyFrameResult {
  const counts = skyCounts();
  const none: SkyFrameResult = { ...counts, strands: 0, strandPath: false, visible: 0 };
  /* Nothing has streamed in yet: not an empty sky, an unbuilt one. */
  if (field.loaded === 0 && field.cells.size === 0) return none;

  const z = view.cam.z;
  const lod = skyLOD(z, zooms.bands, frame.useLOD !== false);

  if (lod === 3) {
    drawSkyClumps(ctx, field, view, paint, counts);
    return { ...counts, strands: 0, strandPath: false, visible: 0 };
  }

  /* Switched off by band, not thinned by crowd — theirs says so and it is the right rule. */
  const strandPath = frame.showEdges && lod <= STRANDS_BAND && z > zooms.strandsFrom && frame.skipEdges !== true;
  const strands = strandPath
    ? drawSkyEdges(ctx, field, view, edges.paint, edges.fan, edges.stamp, lod === 0, edges.focus ?? {})
    : 0;
  if (frame.skipNodes === true) return { ...counts, strands, strandPath, visible: 0 };

  if (lod === 2) {
    const dots = drawSkyDots(ctx, field, view, paint, counts, frame.cull);
    /* Their `_strandPath=false` here: at this band there is no web whatever the zoom said. */
    return { ...counts, strands, strandPath: false, visible: dots };
  }

  drawSkyDiscs(ctx, field, view, paint, counts, lod === 0, labels, frame.cull);
  return { ...counts, strands, strandPath, visible: counts.discs + counts.dots };
}
